import undoable, { excludeAction } from "redux-undo";
import { createSlice, createSelector } from "@reduxjs/toolkit";
import { memoize } from "proxy-memoize";
import { getDefaultReduxState } from "../../middleware/getVizDetails";
import { buildModifiedSheetState } from "../../middleware/buildModifiedSheetState";
import ObjectId from "bson-objectid";

const getInitialState = () => {
  const sheet = getDefaultReduxState({ vizType: "simpleTable" }).newSheetState;
  return {
    _id: new ObjectId().toHexString(),
    name: "New Workbook",
    commonFilters: [],
    hierarchy: [],
    createdOn: new Date().toISOString(),
    selectedSheetId: sheet.ID,
    sheets: [sheet],
    dataSources: [],
  };
};

const initialState = getInitialState();
const reduxWorkbookSlice = createSlice({
  name: "reduxWorkbook",
  initialState,

  reducers: {
    setInitialState: (state, action) => {
      // console.log(state);

      const newWorkbookState = getInitialState();
      // console.log(newWorkbookState._id);
      const { dataSources } = state;
      const { _id, createdOn, selectedSheetId, sheets, name } =
        newWorkbookState;
      state.dataSources = dataSources;
      state._id = _id;
      state.name = name;
      state.createdOn = createdOn;
      state.selectedSheetId = selectedSheetId;
      state.sheets = sheets;
      state.sheets[0].dataSourceID = dataSources[0];
    },
    setLevel2Properties: (state, action) => {
      const { Sheetid, parentKey, reduxKey, value, itemInArray } =
        action.payload;
      // console.log(reduxKey);
      // console.log(value);

      const sheet = state.sheets.find((sheet) => sheet.ID === Sheetid);
      if (!sheet) {
        // console.log("setLevel2ArrayProperties: Sheet not found");
        return;
      }
      // console.log(sheet);

      if (itemInArray) {
        // If the property is not an array, override it with a new array
        if (!Array.isArray(sheet[parentKey]?.[reduxKey])) {
          sheet[parentKey][reduxKey] = [];
        }

        sheet[parentKey][reduxKey].push(value);
      } else {
        if (!sheet[parentKey]) {
          sheet[parentKey] = {};
        }
        sheet[parentKey][reduxKey] = value;
      }
      // console.log(sheet);
      // Custom logic to update sort
      if (sheet.actions && sheet.actions.sort) {
        sheet.actions.sort.forEach((sortItem) => {
          sheet.query.valueColumns.forEach((valueColumn) => {
            // Only update sort when groupByFields length becomes greater than 0
            if (sheet.query.groupByFields.length) {
              // Log to ensure the correct conditions are checked
              // console.log("GroupByFields length is greater than 0");
              // console.log(
              //   "Matching valueColumn._id:",
              //   valueColumn._id === sortItem.columnId
              // );

              if (valueColumn._id === sortItem.columnId) {
                // Update sort column to "Total SUM_Sales" format
                sortItem.column = `Total ${valueColumn.measure.value}_${valueColumn.rename}`;
                // console.log("Updated sortItem:", sortItem);
              }
            } else {
              // console.log(
              //   "GroupByFields length is still 0. Waiting for the change."
              // );
            }
          });
        });
      }
    },
    setLevel2SliderProperty: (state, action) => {
      const { Sheetid, parentKey, reduxKey, value, itemInArray } =
        action.payload;
      const sheet = state.sheets.find((sheet) => sheet.ID === Sheetid);
      if (!sheet) {
        // console.log("setLevel2ArrayProperties: Sheet not found");
        return;
      }

      if (itemInArray) {
        // If the property is not an array, override it with a new array
        if (!Array.isArray(sheet[parentKey]?.[reduxKey])) {
          sheet[parentKey][reduxKey] = [];
        }

        sheet[parentKey][reduxKey].push(value);
      } else {
        if (!sheet[parentKey]) {
          sheet[parentKey] = {};
        }
        sheet[parentKey][reduxKey] = value;
      }
    },
    removeLevel2Properties: (state, action) => {
      const { Sheetid, parentKey, reduxKey, value, viztype } = action.payload;

      const sheet = state.sheets.find((sheet) => sheet.ID === Sheetid);
      if (!sheet) {
        // console.log("removeLevel2ArrayProperties: Sheet not found");
        return;
      }
      // console.log(value);

      const property = sheet[parentKey]?.[reduxKey];
      if (!Array.isArray(property)) {
        // console.log(
        //   "removeLevel2ArrayProperties: Invalid property or not an array"
        // );
        return;
      }

      // Remove the item from the parentKey's array using the uuid
      sheet[parentKey][reduxKey] = property.filter(
        (item) => item.uuid !== value.uuid
      );

      // Now also remove from actions.sort if the value._id matches sortItem._id
      if (sheet.actions && sheet.actions.sort) {
        if (sheet.actions && sheet.actions.sort && reduxKey === "rows") {
          // console.log(value.columnName);

          sheet.actions.sort = sheet.actions.sort.filter((sortItem) => {
            // console.log(value);
            // console.log(sortItem);
            // console.log(sortItem.columnId === value._id);
            return sortItem.columnId !== value._id;
          });
        } else if (reduxKey === "groupByFields") {
          sheet.actions.sort = sheet.actions.sort.filter((sortItem) => {
            return sortItem.columnId !== reduxKey;
          });
        } else if (reduxKey === "valueColumns") {
          sheet.actions.sort = sheet.actions.sort.filter((sortItem) => {
            return sortItem.columnId !== value._id;
          });
        }
      }
      // Now also remove from actions.quickTable if the quickItem.nanoId matches value.nanoId
      if (sheet.actions && sheet.actions.quickTable) {
        if (reduxKey === "valueColumns") {
          sheet.actions.quickTable = sheet.actions.quickTable.filter(
            (quickItem) => {
              // console.log(quickItem.nanoId); // Log the nanoId of each item in quickTable
              // console.log(value.nanoId); // Log the nanoId value you're filtering against

              // Return false to remove the item if nanoId matches
              return quickItem.nanoId !== value.nanoId;
            }
          );
        }
      }

      if (reduxKey === "groupByFields" && viztype === "cordinatemap") {
        sheet.customization.rows = {};
      }
    },
    removeLevel2PropertiesBynanoId: (state, action) => {
      const {
        Sheetid,
        parentKey,
        reduxKey,
        value,
        hierarchy,
        vizType,
        isHierarchy = false,
      } = action.payload;
      // console.log(reduxKey);
      // console.log(value);
      // // console.log(hierarchy);
      const chartTypes = [
        "piechart",
        "linechart",
        "areachart",
        "stackedbarchart",
        "scatterchart",
        "combochart",
        "stackedbarchartimage",
      ];
      const sheet = state.sheets.find((sheet) => sheet.ID === Sheetid);
      if (!sheet) {
        // console.log("removeLevel2ArrayProperties: Sheet not found");
        return;
      }
      const property = sheet[parentKey]?.[reduxKey];
      // console.log(property.length);

      if (reduxKey === "conditionalFormatting") {
        // Remove the item from the parentKey's array using the nanoId
        sheet[parentKey][reduxKey] = property.filter(
          (item) => item.columnNanoId !== value.columnNanoId
        );
      } else {
        if (
          ["rows", "groupByFields"].includes(reduxKey) &&
          value.dataType === "Date" &&
          property.length === 1
        ) {
          const filteredHierarchy = hierarchy.filter((hItem) =>
            hItem.hierarchyArray.every((hAItem) => {
              if (hAItem.dataType === "Date") {
                return !(
                  `${hAItem.rename}${hAItem.dateFormat.value}` ===
                    `${value.rename}${value.dateFormat.value}` &&
                  hAItem.uuid === value.uuid
                );
              }
              return true; // keep if not a "Date" type
            })
          );
          // console.log(isHierarchy);
          // console.log(filteredHierarchy.length);
          if (!isHierarchy) {
            // Update the state
            state.hierarchy = filteredHierarchy;
          }
        }

        // Remove the item from the parentKey's array using the nanoId
        sheet[parentKey][reduxKey] = property.filter((item) => {
          // console.log(item);
          return item.nanoId !== value.nanoId;
        });
        // Now also remove from actions.sort if the value._id matches sortItem._id
        if (sheet.actions && sheet.actions.sort) {
          if (reduxKey === "rows") {
            if (property.length === 1) {
              const valuesArray = sheet["query"]?.["valueColumns"];
              if (Array.isArray(valuesArray) && valuesArray.length) {
                const valuesArrayFirstItem = valuesArray[0];
                sheet.actions.sort = [
                  {
                    column: `${valuesArrayFirstItem.measure.value}_${valuesArrayFirstItem.columnName}`,
                    dataType: valuesArrayFirstItem.dataType,
                    order: "DESC",
                    _id: valuesArrayFirstItem._id,
                    columnId: valuesArrayFirstItem.nanoId,
                  },
                ];
              } else {
                // console.log("set sort blank array");
                sheet.actions.sort = [];
              }
            }
            sheet.actions.sort = sheet.actions.sort.filter((sortItem) => {
              return sortItem.columnId !== value.nanoId;
            });
          } else if (reduxKey === "groupByFields") {
            if (property.length === 1) {
              const valuesArray = sheet["query"]?.["valueColumns"];
              if (Array.isArray(valuesArray) && valuesArray.length) {
                const valuesArrayFirstItem = valuesArray[0];
                sheet.actions.sort = [
                  {
                    column: `${valuesArrayFirstItem.measure.value}_${valuesArrayFirstItem.columnName}`,
                    dataType: valuesArrayFirstItem.dataType,
                    order: "DESC",
                    _id: valuesArrayFirstItem._id,
                    columnId: valuesArrayFirstItem.nanoId,
                  },
                ];
              } else {
                // console.log("set sort blank array");
                sheet.actions.sort = [];
              }
            }
            // console.log(
            //   sheet.actions.sort.filter((sortItem) => {
            //     return sortItem.columnId !== reduxKey;
            //   })
            // );

            sheet.actions.sort = sheet.actions.sort.filter((sortItem) => {
              return sortItem.columnId !== reduxKey;
            });
          } else if (reduxKey === "valueColumns") {
            const columnName = `${value.measure.value}_${value.rename}`;

            sheet.actions.sort = sheet.actions.sort.filter((sortItem) => {
              // split on space and take the second part
              const [, secondToken] = sortItem.column.split(" ");

              // if it matches our target columnName, filter it out (i.e. return false)
              if (secondToken === columnName) {
                // console.log(
                //   `Removing sortItem because "${secondToken}" === "${columnName}"`,
                //   sortItem
                // );
                return false;
              }

              // otherwise keep it
              return true;
            });
          }
        }
        // Now also remove from actions.quickTable if the quickItem.nanoId matches value.nanoId
        if (sheet.actions && sheet.actions.quickTable) {
          if (reduxKey === "valueColumns") {
            sheet.actions.quickTable = sheet.actions.quickTable.filter(
              (quickItem) => {
                return quickItem.nanoId !== value.nanoId;
              }
            );
          }
        }
        if (
          Array.isArray(sheet["customization"]["conditionalFormatting"]) &&
          sheet["customization"]["conditionalFormatting"].length
        ) {
          sheet["customization"]["conditionalFormatting"] = sheet[
            "customization"
          ]["conditionalFormatting"].filter(
            (item) => item.columnNanoId !== value.nanoId
          );
        }
      }

      if (!Array.isArray(property)) {
        // console.log(
        //   "removeLevel2ArrayProperties: Invalid property or not an array"
        // );
        return;
      }
      // console.log(property);
    },
    updateLevel2Properties: (state, action) => {
      const { Sheetid, parentKey, reduxKey, value, itemInArray, index } =
        action.payload;
      // // console.log(reduxKey);
      // // console.log(itemInArray);
      // console.log(value);

      const sheet = state.sheets.find((sheet) => sheet.ID === Sheetid);
      if (!sheet) {
        // console.log("updateLevel2Properties: Sheet not found");
        return;
      }
      // // console.log(sheet);

      if (itemInArray) {
        if (Array.isArray(sheet[parentKey]?.[reduxKey])) {
          // console.log(sheet[parentKey][reduxKey][index]);

          if (
            index !== undefined &&
            sheet[parentKey][reduxKey] !== undefined &&
            sheet[parentKey][reduxKey][index] !== undefined
          ) {
            sheet[parentKey][reduxKey][index] = value;
          } else {
            // console.log(
            //   "updateLevel2Properties: Index or parentKey or reduxKey not found in array"
            // );
          }
        } else {
          // console.log("updateLevel2Properties: Parent key is not an array");
        }
      } else {
        if (sheet[parentKey] && sheet[parentKey][reduxKey] !== undefined) {
          sheet[parentKey][reduxKey] = value;
        } else {
          // console.log(
          //   "updateLevel2Properties: parentKey or reduxKey not found"
          // );
        }
      }

      if (sheet.actions && sheet.actions.sort.length) {
        // console.log(value);
        // console.log(sheet.actions.sort);
        const updatedSortItemIndex = sheet.actions.sort.findIndex(
          (sortItem) => {
            if (sortItem.columnId) {
              return sortItem.columnId === value.nanoId;
            } else {
              return sortItem.nanoId === value.nanoId;
            }
          }
        );
        // console.log(updatedSortItemIndex);
        if (updatedSortItemIndex !== -1) {
          if (value.dataType === "Date") {
            const newColumn = `${value.rename}${value.dateFormat.value}`;
            const newFormat = value.dateFormat.value;
            const newYearType = value.yearType;
            const updatedSortItem = {
              ...sheet.actions.sort[updatedSortItemIndex],
              column: newColumn,
              format: newFormat,
              yearType: newYearType,
            };
            // console.log(updatedSortItem);
            sheet.actions.sort[updatedSortItemIndex] = updatedSortItem;
          }
          if (value.dataType === "Number") {
            const updatedSortItem = {
              ...sheet.actions.sort[updatedSortItemIndex],
              column: `${value.measure.value}_${value.columnName}`,
            };
            // console.log(updatedSortItem);
            sheet.actions.sort[updatedSortItemIndex] = updatedSortItem;
          }
        } else {
          // console.log("index is -1");
        }
      }

      if (sheet.actions && sheet.actions.quickTable) {
        if (reduxKey === "valueColumns") {
          const itemFoundInQuickTable = sheet.actions.quickTable.findIndex(
            (quickItem) => quickItem.nanoId === value.nanoId
          );
          // console.log(itemFoundInQuickTable);
          if (itemFoundInQuickTable === -1) {
            return;
          }
          sheet.actions.quickTable[
            itemFoundInQuickTable
          ].quickTableKey = `${value.measure.value}_${value.rename}`;
        } else {
          // console.log("redux is not valueColumns");
        }
      } else {
        // console.log("sheet.actions && sheet.actions.quickTable is not aviable");
      }
      // if (value.dataType === "Date") {
      //   const updatedHierarchy = state.hierarchy.map((hItem) => {
      //     // Check if this hierarchy item contains the target nanoId
      //     if (
      //       hItem.hierarchyArray.some(
      //         (hAItem) => hAItem.nanoId === value.nanoId
      //       )
      //     ) {
      //       return {
      //         ...hItem,
      //         hierarchyArray: hItem.hierarchyArray.map((hAItem) =>
      //           hAItem.nanoId === value.nanoId
      //             ? {
      //                 ...hAItem,
      //                 dateFormat: value.dateFormat,
      //                 dateUnit: value.dateUnit,
      //                 yearType: value.yearType,
      //               }
      //             : hAItem
      //         ),
      //       };
      //     }
      //     return hItem;
      //   });
      //   state.hierarchy = updatedHierarchy;
      // }
    },
    setWorkbookProperty: (state, action) => {
      const { itemInArray, key, value } = action.payload;

      if (itemInArray) {
        state[key].push(value);
      } else {
        state[key] = value;
      }
    },
    setCommonFilters: (state, action) => {
      const { value, operation, Sheetid, itemInArray } = action.payload;
      // console.log(value);
      if (itemInArray) {
        // console.log(state);
        switch (operation) {
          case "ADD":
            state.commonFilters = value;
            break;

          default:
            break;
        }
      } else {
        let {
          visibleFilter = false,
          isCommonFilter = false,
          nanoId,
          filterComponentType,
        } = value;
        // console.log(isCommonFilter);

        // console.log(filterComponentType);

        const targetSheet = state.sheets.find((sheet) => sheet.ID === Sheetid);
        if (!targetSheet) {
          // console.log("Sheets Not Found");
        }

        const searchOldItem = () => {
          //search in sheet filter and return if found
          const sheetFilterIndex =
            targetSheet?.query &&
            targetSheet.query.filters.findIndex((item) => {
              // console.log(item.nanoId);
              // console.log(nanoId);
              // console.log(item.nanoId === nanoId);
              if (item.nanoId && nanoId) {
                return item.nanoId === nanoId;
              }
            });
          // console.log(sheetFilterIndex);

          const sheetFilterObject = targetSheet.query.filters[sheetFilterIndex];

          // console.log(sheetFilterObject);

          if (sheetFilterIndex !== -1) {
            // // console.log({
            //   oldItemIndex: sheetFilterIndex,
            //   oldFilterObject: sheetFilterObject,
            //   location: "sheet",
            // });

            return {
              oldItemIndex: sheetFilterIndex,
              oldFilterObject: sheetFilterObject,
              location: "sheet",
            };
          }

          //search in common filter
          const commonFilterIndex = state.commonFilters.findIndex(
            (item) => item.nanoId === nanoId
          );
          // console.log(commonFilterIndex);

          const commonFilterObject = state.commonFilters[commonFilterIndex];

          // console.log(commonFilterObject);
          if (commonFilterIndex !== -1) {
            // console.log({
            //   oldItemIndex: commonFilterIndex,
            //   oldFilterObject: commonFilterObject,
            //   location: "common",
            // });

            return {
              oldItemIndex: commonFilterIndex,
              oldFilterObject: commonFilterObject,
              location: "common",
            };
          }
        };

        switch (operation) {
          case "ADD":
            //simply add the item in the found sheet and add the default props if not found already
            if (!isCommonFilter) {
              //sheetFilter
              targetSheet["query"]["filters"].push({
                ...value,
                visibleFilter,
                isCommonFilter,
                Sheetid,
                filterComponentType,
              });
            } else {
              //common filter
              state.commonFilters.push({
                ...value,
                visibleFilter,
                isCommonFilter,
                Sheetid,
                filterComponentType,
              });
            }

            break;
          case "UPDATE":
            //get the old filter item
            const { oldItemIndex, oldFilterObject, location } =
              searchOldItem() !== undefined && searchOldItem();
            // console.log(oldItemIndex);
            // console.log(oldFilterObject);
            // console.log(location);
            let { isCommonFilter: oldIsCommonFilter } =
              oldFilterObject !== undefined && oldFilterObject;
            // if (!oldIsCommonFilter) {
            //   //previously it was a sheet filter
            //   // console.log(!oldIsCommonFilter);
            //   // console.log("i ran");
            //   oldIsCommonFilter = isCommonFilter;
            // }
            // console.log(oldIsCommonFilter, isCommonFilter);

            if (isCommonFilter !== oldIsCommonFilter) {
              if (location === "common" && oldItemIndex !== -1) {
                // console.log(location);
                // console.log(oldItemIndex);
                // console.log("removed from common and added sheet");

                //remove from common
                state.commonFilters.splice(oldItemIndex, 1);

                //add in sheet
                targetSheet["query"]["filters"].push({
                  ...value,
                  visibleFilter,
                  isCommonFilter,
                  Sheetid,
                  filterComponentType,
                });
              } else if (location === "sheet" && oldItemIndex !== -1) {
                // console.log(location);
                // console.log(oldItemIndex);
                // console.log("removed from sheet added in common");

                //remove from sheet
                targetSheet["query"]["filters"].splice(oldItemIndex, 1);

                //add in common
                state.commonFilters.push({
                  ...value,
                  visibleFilter,
                  isCommonFilter,
                  Sheetid,
                  filterComponentType,
                });
              } else {
                // console.log("item not found, hence cannot update");
              }
              //toggle filter location
            } else {
              //update item in respective location
              if (location === "common" && oldItemIndex !== -1) {
                //update in commonFilters
                // console.log(value);

                state.commonFilters[oldItemIndex] = value;
              } else if (location === "sheet" && oldItemIndex !== -1) {
                //update in sheet filter
                // console.log(value);
                // console.log(oldItemIndex);

                targetSheet.query.filters[oldItemIndex] = value;
              } else {
                // console.log("item not found, hence cannot update");
              }
            }

            break;
          case "REMOVE":
            const { oldItemIndex: oldItemIndexR, location: locationR } =
              searchOldItem();
            // console.log(oldItemIndexR, locationR);

            if (locationR === "common" && oldItemIndexR !== -1) {
              //remove from common
              // console.log(
              //   `${value.rename} filter found in common filters at index ${oldItemIndexR}`
              // );
              state.commonFilters.splice(oldItemIndexR, 1);
            } else if (locationR === "sheet" && oldItemIndexR !== -1) {
              //remove from sheet
              // console.log(
              //   `${value.rename} filter found in sheets filters at index ${oldItemIndexR}`
              // );
              targetSheet["query"]["filters"].splice(oldItemIndexR, 1);
            } else {
              // console.log("item not found, hence cannot update");
            }

            break;
          default:
            //should never happen
            break;
        }
      }
    },
    updateQueryItemForHierarchy: (state, action) => {
      const { value } = action.payload;
      state.sheets = state.sheets.map((sheet) => {
        if (sheet.query) {
          let { rows, groupByFields } = sheet.query;
          const hierarchyItemInRowsAtIndex =
            Array.isArray(rows) &&
            rows.findIndex(
              // ✅ Fixed method name
              (rItem) => rItem.uuid === value.uuid
            );
          const hierarchyItemInGroupByFieldsAtIndex =
            Array.isArray(groupByFields) &&
            groupByFields.findIndex(
              // ✅ Fixed method name
              (rItem) => rItem.uuid === value.uuid
            );

          // console.log(hierarchyItemInRowsAtIndex);
          // console.log(hierarchyItemInGroupByFieldsAtIndex);

          if (hierarchyItemInRowsAtIndex !== -1) {
            rows[hierarchyItemInRowsAtIndex] = {
              ...rows[hierarchyItemInRowsAtIndex],
              nanoId: value.nanoId,
            };
          }
          if (hierarchyItemInGroupByFieldsAtIndex !== -1) {
            groupByFields[hierarchyItemInGroupByFieldsAtIndex] = {
              ...groupByFields[hierarchyItemInGroupByFieldsAtIndex],
              nanoId: value.nanoId,
            };
          }

          return sheet;
        } else {
          console.error(
            "sheet.query is undefined for updateQueryItemForHierarchy"
          );
        }
      });
    },
    deleteTemporaryFilters: (state, action) => {
      const { sourceSheet, allTempFiltersBoolean } = action.payload;
      // console.log({ sourceSheet, allTempFiltersBoolean });

      if (allTempFiltersBoolean) {
        // Iterate through each sheet in the state
        state.sheets.forEach((sheet) => {
          if (sheet.sheetType !== "dashboard") {
            for (let i = sheet.query.filters.length - 1; i >= 0; i--) {
              if (sheet.query.filters[i].filterType === "temporary") {
                sheet.query.filters.splice(i, 1);
              }
            }
          }
        });
      } else {
        if (!sourceSheet) {
          return "sourceSheetId is required";
        }
        if (state.sheets) {
          state.sheets.forEach((sheet) => {
            if (sheet.sheetType !== "dashboard") {
              for (let i = sheet.query.filters.length - 1; i >= 0; i--) {
                if (
                  sheet.query.filters[i].filterType === "temporary" &&
                  sheet.query.filters[i].Sheetid === sourceSheet
                ) {
                  sheet.query.filters.splice(i, 1);
                }
              }
            }
          });
        }
        // Iterate through each sheet in the state

        // console.log("filter");
      }
    },
    setFiltersToOtherSheets: (state, action) => {
      const {
        columnName,
        rename,
        dataType,
        uuid,
        Sheetid,
        nanoId,
        selectedOperator,
        selectedFilterValues,
        filterType,
        vizType,
        filterArray,
        format,
        properties,
        dataSourceID,
        columnType,
        formula,
      } = action.payload;
      // console.log(properties);

      // console.log(filterArray);

      const {
        action: requiredAction,
        targetSheets,
        sourceSheet,
        isAllSheets,
      } = properties || {};

      const dashboardSheet = state.sheets.find(
        (sheet) => sheet.sheetType === "dashboard"
      );
      state.sheets = state.sheets.map((sheet) => {
        // // console.log(sheet);
        if (sheet.query) {
          let existingTemporaryFilter = sheet.query.filters.find(
            (filter) =>
              filter.filterType === "temporary" && filter.Sheetid === Sheetid
          );
          // const existingSheetFilters = sheet.query.filters.filter(
          //   (filterItem) => filterItem.filterType !== "temporary"
          // );
          const existingSheetFilters = sheet.query.filters;
          if (properties !== undefined) {
            //if properties are defined that's means actions will be applied
            if (requiredAction === "GoToSheet") {
              const targetSheetId = targetSheets[0]?.Sheetid;
              if (sheet.ID === targetSheetId) {
                // console.log(sheet.query.filters);
                const sheetParmanentFilters = sheet.query.filters.filter(
                  (filterItem) => filterItem.filterType !== "temporary"
                );
                // console.log(sheetParmanentFilters);
                //["stackedbarchart", "stackedbarchartimage"].includes(vizType)
                if (
                  ["stackedbarchart", "stackedbarchartimage"].includes(vizType)
                ) {
                  let resultantFilterArray = [...sheetParmanentFilters]; // Start with existing filters

                  //check is there any temporary filter present in the target sheet
                  // console.log(existingTemporaryFilter);
                  // console.log(resultantFilterArray);

                  // const resultantFilterArray
                  // if yes then remove the existed temporary filter and the merge existing filter with temporary filter with filterArray
                  // Check presence of filterArray items in sheet.query.filters

                  const isFilter0Present = sheet.query.filters.includes(
                    filterArray[0]
                  );
                  const isFilter1Present = sheet.query.filters.includes(
                    filterArray[1]
                  );

                  if (!isFilter0Present && !isFilter1Present) {
                    // Both filters are missing, merge existingSheetFilters and filterArray
                    // console.log("Both filters are missing. Merging filters.");
                    resultantFilterArray = [
                      ...sheetParmanentFilters,
                      ...filterArray,
                    ];
                  } else if (!isFilter0Present || !isFilter1Present) {
                    // One filter is missing, push the missing item
                    // console.log(
                    //   "One filter is missing. Adding the missing filter."
                    // );
                    if (!isFilter0Present) {
                      resultantFilterArray.push(filterArray[0]);
                    }
                    if (!isFilter1Present) {
                      resultantFilterArray.push(filterArray[1]);
                    }
                  } else {
                    // Both filters are present
                    // console.log("Both filters are present. No changes made.");
                  }

                  // console.log(resultantFilterArray);

                  // Update sheet.query.filters with the resultant filter array
                  sheet.query.filters = resultantFilterArray;
                  // console.log(sheet);
                } else {
                  if (existingTemporaryFilter) {
                    existingTemporaryFilter.columnName = columnName;
                    existingTemporaryFilter.rename = rename;
                    existingTemporaryFilter.dataType = dataType;
                    existingTemporaryFilter.uuid = uuid;
                    existingTemporaryFilter.Sheetid = Sheetid;
                    existingTemporaryFilter.nanoId = nanoId;
                    existingTemporaryFilter.selectedOperator = selectedOperator;
                    existingTemporaryFilter.selectedFilterValues =
                      selectedFilterValues;
                    existingTemporaryFilter.format = format;
                    existingTemporaryFilter.columnType = columnType;
                    existingTemporaryFilter.formula = formula;
                  } else {
                    sheet.query.filters.push({
                      columnName: columnName,
                      rename: rename,
                      dataType: dataType,
                      uuid: uuid,
                      Sheetid: Sheetid,
                      nanoId: nanoId,
                      selectedOperator: selectedOperator,
                      selectedFilterValues: selectedFilterValues,
                      filterType: filterType,
                      format: format,
                      dataSourceID,
                      columnType,
                      formula,
                    });
                  }
                }
              }
            }
            if (requiredAction === "Filters") {
              const targetSheetIds = isAllSheets
                ? state.sheets
                    .filter((filter) => filter.sheetType !== "dashboard")
                    .map((sheet) => sheet.ID) // Apply to all sheets if isAllSheets is true
                : targetSheets !== undefined &&
                  targetSheets.map((targetSheet) => targetSheet.Sheetid);
              // console.log(targetSheetIds);
              if (targetSheetIds.includes(sheet.ID)) {
                if (
                  ["stackedbarchart", "stackedbarchartimage"].includes(vizType)
                ) {
                  // console.log(filterArray);
                  // console.log(existingSheetFilters);
                  // console.log(existingTemporaryFilter);

                  let resultantFilterArray;

                  // Determine if filterArray contains any invalid filters
                  const hasInvalidFilters = filterArray.some(
                    (item) =>
                      !item.selectedFilterValues ||
                      item.selectedFilterValues.includes(undefined)
                  );

                  if (hasInvalidFilters) {
                    // If invalid filters exist, remove all filters from existingSheetFilters with matching Sheetid
                    const invalidSheetIds = filterArray
                      .filter(
                        (item) =>
                          !item.selectedFilterValues ||
                          item.selectedFilterValues.includes(undefined)
                      )
                      .map((item) => item.Sheetid);

                    resultantFilterArray = existingSheetFilters.filter(
                      (filter) => !invalidSheetIds.includes(filter.Sheetid)
                    );
                  } else if (filterArray.length === 0) {
                    // If filterArray is empty, remove all "temporary" filters
                    resultantFilterArray = existingSheetFilters.filter(
                      (filter) => filter.filterType !== "temporary"
                    );
                  } else {
                    // Merge existingSheetFilters + filterArray
                    resultantFilterArray = [...existingSheetFilters];

                    filterArray.forEach((newFilter) => {
                      const index = resultantFilterArray.findIndex(
                        (existingFilter) =>
                          existingFilter.Sheetid === newFilter.Sheetid &&
                          existingFilter.columnName === newFilter.columnName
                      );

                      if (index !== -1) {
                        // Override the existing filter with the new filter
                        resultantFilterArray[index] = newFilter;
                      } else {
                        // Add the new filter to the resultant array
                        resultantFilterArray.push(newFilter);
                      }
                    });
                  }

                  // console.log(resultantFilterArray);
                  sheet.query.filters = resultantFilterArray;
                } else {
                  // console.log(existingTemporaryFilter);
                  if (existingTemporaryFilter) {
                    existingTemporaryFilter.columnName = columnName;
                    existingTemporaryFilter.rename = rename;
                    existingTemporaryFilter.dataType = dataType;
                    existingTemporaryFilter.uuid = uuid;
                    existingTemporaryFilter.Sheetid = Sheetid;
                    existingTemporaryFilter.nanoId = nanoId;
                    existingTemporaryFilter.selectedOperator = selectedOperator;
                    existingTemporaryFilter.selectedFilterValues =
                      selectedFilterValues;
                    existingTemporaryFilter.format = format;
                    existingTemporaryFilter.columnType = columnType;
                    existingTemporaryFilter.formula = formula;
                  } else {
                    sheet.query.filters.push({
                      columnName: columnName,
                      rename: rename,
                      dataType: dataType,
                      uuid: uuid,
                      Sheetid: Sheetid,
                      nanoId: nanoId,
                      selectedOperator: selectedOperator,
                      selectedFilterValues: selectedFilterValues,
                      filterType: filterType,
                      format: format,
                      dataSourceID,
                      columnType,
                      formula,
                    });
                  }
                }
              }
            }
          } else {
            // if properties are undefined that's means chart default filter feature is running
            // if vizType is stackedBarChart SHEETID will filterArray[0].Sheetid other wise Sheetid
            const sourceSheetId =
              ["stackedbarchart", "stackedbarchartimage"].includes(vizType) &&
              filterArray.length
                ? filterArray[0].Sheetid
                : Sheetid;
            // find the dashboard sheet ID
            const applyFilter2OthersSheets =
              dashboardSheet !== undefined
                ? sheet.ID !== sourceSheetId && sheet.ID !== dashboardSheet?.ID
                : sheet.ID !== sourceSheetId;
            // filters will apply on others sheets except current sheet and dashboard sheet
            if (applyFilter2OthersSheets) {
              // if vizType is stackBarChart that means a whole filter array will be set including the already applied filters
              if (
                ["stackedbarchart", "stackedbarchartimage"].includes(vizType)
              ) {
                // console.log(filterArray);
                // console.log(existingSheetFilters);
                // console.log(existingTemporaryFilter);

                let resultantFilterArray;

                // Determine if filterArray contains any invalid filters
                const hasInvalidFilters = filterArray.some(
                  (item) =>
                    !item.selectedFilterValues ||
                    item.selectedFilterValues.includes(undefined)
                );

                if (hasInvalidFilters) {
                  // If invalid filters exist, remove all filters from existingSheetFilters with matching Sheetid
                  const invalidSheetIds = filterArray
                    .filter(
                      (item) =>
                        !item.selectedFilterValues ||
                        item.selectedFilterValues.includes(undefined)
                    )
                    .map((item) => item.Sheetid);

                  resultantFilterArray = existingSheetFilters.filter(
                    (filter) => !invalidSheetIds.includes(filter.Sheetid)
                  );
                } else if (filterArray.length === 0) {
                  // If filterArray is empty, remove all "temporary" filters
                  resultantFilterArray = existingSheetFilters.filter(
                    (filter) => filter.filterType !== "temporary"
                  );
                } else {
                  // Merge existingSheetFilters + filterArray
                  resultantFilterArray = [...existingSheetFilters];

                  filterArray.forEach((newFilter) => {
                    const index = resultantFilterArray.findIndex(
                      (existingFilter) =>
                        existingFilter.Sheetid === newFilter.Sheetid &&
                        existingFilter.columnName === newFilter.columnName
                    );

                    if (index !== -1) {
                      // Override the existing filter with the new filter
                      resultantFilterArray[index] = newFilter;
                    } else {
                      // Add the new filter to the resultant array
                      resultantFilterArray.push(newFilter);
                    }
                  });
                }

                // console.log(resultantFilterArray);
                sheet.query.filters = resultantFilterArray;
              } else {
                if (existingTemporaryFilter) {
                  existingTemporaryFilter.columnName = columnName;
                  existingTemporaryFilter.rename = rename;
                  existingTemporaryFilter.dataType = dataType;
                  existingTemporaryFilter.uuid = uuid;
                  existingTemporaryFilter.Sheetid = Sheetid;
                  existingTemporaryFilter.nanoId = nanoId;
                  existingTemporaryFilter.selectedOperator = selectedOperator;
                  existingTemporaryFilter.dataSourceID = dataSourceID;
                  existingTemporaryFilter.selectedFilterValues =
                    selectedFilterValues;
                  existingTemporaryFilter.format = format;
                  existingTemporaryFilter.columnType = columnType;
                  existingTemporaryFilter.formula = formula;
                } else {
                  sheet.query.filters.push({
                    columnName: columnName,
                    rename: rename,
                    dataType: dataType,
                    uuid: uuid,
                    Sheetid: Sheetid,
                    nanoId: nanoId,
                    selectedOperator: selectedOperator,
                    selectedFilterValues: selectedFilterValues,
                    filterType: filterType,
                    format: format,
                    dataSourceID,
                    columnType,
                    formula,
                  });
                }
              }
            } else {
              // console.log("i do not know what is happening");
            }
          }
        } else {
          console.error("sheet query in undefined");
        }
        // console.log(sheet);

        return sheet;
      });
    },
    deleteAction: (state, action) => {
      const { Sheetid, parentKey, reduxKey, uuid } = action.payload;

      // console.log(Sheetid, parentKey, reduxKey, uuid);
      // console.log(state.sheets);

      // Find the sheet with the given Sheetid
      const sheetIndex = state.sheets.findIndex(
        (sheet) => sheet.ID === Sheetid
      );
      if (sheetIndex !== -1 && reduxKey) {
        // Check if the reduxKey is either 'onClick' or 'onDoubleClick' and clear the content
        if (reduxKey === "onClick" || reduxKey === "onDoubleClick") {
          state.sheets[sheetIndex].actions[reduxKey] = {};
        }
      }
    },
    updateWorkbookProperty: (state, action) => {
      const { itemInArray, key, value } = action.payload;
    },
    setDataSource: (state, action) => {
      const { dataSourceID, initial = false } = action.payload;
      // console.log(initial, dataSourceID);
      if (initial) {
        state.dataSources = [dataSourceID];
        state.sheets.forEach((item) => (item.dataSourceID = dataSourceID));
      } else {
        state.dataSources.push(dataSourceID);
      }
    },
    updateL1Property: (state, action) => {
      const { SheetId, key, value, currentVizTpe } = action.payload;
      // console.log(key, value, currentVizTpe);
      const sheet = state.sheets.find((sheet) => sheet.ID === SheetId);
      // console.log(sheet);

      if (!sheet) {
        // console.log("updateL1Property: Sheet not found");
        return;
      }
      switch (key) {
        case "VizType":
          let requiredSheet = null;
          if (value === currentVizTpe) {
            requiredSheet = sheet;
          } else {
            requiredSheet = buildModifiedSheetState(sheet, value);
          }
          const sheetIndex = state.sheets.findIndex((s) => s.ID === SheetId);
          // console.log(requiredSheet);

          if (sheetIndex !== -1) {
            state.sheets[sheetIndex] = requiredSheet;
          }
          break;
        default:
          sheet[key] = value;
          break;
      }
    },
    RemoveSheet: (state, action) => {
      const { sheetId } = action.payload;
      if (sheetId === state.selectedSheetId) {
        state.sheets = state.sheets.filter((sheet) => sheet.ID !== sheetId);
        state.selectedSheetId = state.sheets[0].ID;
      } else {
        state.sheets = state.sheets.filter((sheet) => sheet.ID !== sheetId);
      }
    },
    CreateSheet: (state, action) => {
      const { Action, ID, newDataSourceID, isDataSourceChanging } =
        action.payload;
      // console.log(Action);
      // console.log(newDataSourceID);
      // console.log(isDataSourceChanging);

      switch (Action) {
        case "CreateSheet":
          const existingSheetNames = state.sheets
            .filter((sheet) => sheet.SheetName.startsWith("Sheet"))
            .map((sheet) => sheet.SheetName);

          // Find the maximum sheet number, even if sheets are deleted
          let maxSheetNumber = 0;
          existingSheetNames.forEach((sheetName) => {
            const match = sheetName.match(/^Sheet\s(\d+)$/); // Match "Sheet X"
            if (match) {
              const number = parseInt(match[1], 10);
              maxSheetNumber = Math.max(maxSheetNumber, number);
            }
          });

          // Use the next number in sequence based on max sheet number
          const nextSheetNumber = maxSheetNumber + 1;
          const defaultSheetName = `Sheet ${nextSheetNumber}`;
          if (isDataSourceChanging) {
            // console.log("a new sheet will create with new dataSourceID");
            const sheetObject = getDefaultReduxState({
              vizType: "simpleTable",
              dataSourceID: newDataSourceID, // Adjust dataSourceID as necessary
              sheetName: defaultSheetName,
            }).newSheetState;

            state.sheets.push(sheetObject);
            state.selectedSheetId = sheetObject.ID;
          } else {
            // console.log("a new sheet will create with old dataSourceID");
            // console.log(
            //   getDefaultReduxState({
            //     vizType: "simpleTable",
            //     dataSourceID: state.dataSources[0], // Adjust dataSourceID as necessary
            //     sheetName: defaultSheetName,
            //   })
            // );

            const sheetObject = getDefaultReduxState({
              vizType: "simpleTable",
              dataSourceID: state.dataSources[0], // Adjust dataSourceID as necessary
              sheetName: defaultSheetName,
            }).newSheetState;
            // console.log(sheetObject);
            state.sheets.push(sheetObject);
            state.selectedSheetId = sheetObject.ID;
          }
          break;

        case "createDashboard":
          let maxDashboardNumber = 0;
          let foundBaseDashboard = false;
          // Check for existing dashboards and determine the highest number
          state.sheets.forEach((sheet) => {
            if (sheet.SheetName === "Dashboard") {
              foundBaseDashboard = true; // Track if "Dashboard" exists
            }
            const match = sheet.SheetName.match(/^Dashboard\s*(\d+)$/);
            if (match) {
              const number = parseInt(match[1], 10);
              maxDashboardNumber = Math.max(maxDashboardNumber, number);
            }
          });
          // Create the new dashboard name
          const dashboardSheetName = foundBaseDashboard
            ? `Dashboard ${maxDashboardNumber + 1}`
            : "Dashboard";
          const nanoId = new ObjectId().toHexString();
          state.sheets.push({
            sheetType: "dashboard",
            SheetName: dashboardSheetName,
            ID: nanoId,
          });
          // Set the newly created dashboard as the selected one
          state.selectedSheetId = nanoId;
          break;

        case "Duplicate":
          const { ID } = action.payload;
          const sheetToDuplicate = state.sheets.find(
            (sheet) => sheet.ID === ID
          );

          if (sheetToDuplicate) {
            const baseName = sheetToDuplicate.SheetName.split(" (")[0]; // Get the base name before any parenthesis
            const existingNames = state.sheets
              .filter((sheet) => sheet.SheetName.startsWith(baseName))
              .map((sheet) => sheet.SheetName);

            const generateNewName = (name) => {
              const regex = /\((\d+)\)$/;
              const match = name.match(regex);
              if (match) {
                const number = parseInt(match[1], 10) + 1;
                return name.replace(regex, `(${number})`);
              } else {
                return `${name} (1)`;
              }
            };

            let newName = `${baseName} (1)`;
            while (existingNames.includes(newName)) {
              newName = generateNewName(newName);
            }

            const duplicatedSheet = {
              ...sheetToDuplicate,
              ID: new ObjectId().toHexString(),
              SheetName: newName,
            };

            state.sheets.push(duplicatedSheet);
            state.selectedSheetId = duplicatedSheet.ID;
          } else {
            console.error("Sheet to duplicate not found");
          }
          break;

        default:
          break;
      }
    },
    updateCalculatedField: (state, action) => {
      const { value } = action.payload;
      const sheets = state.sheets;
      sheets.forEach((sheet) => {
        const { query } = sheet;
        const updateFieldInArray = (arr) => {
          return arr.map((field) => {
            if (field.uuid === value.uuid) {
              return { ...field, ...value };
            }
            return field;
          });
        };
        query.rows = updateFieldInArray(query.rows);
        query.valueColumns = updateFieldInArray(query.valueColumns);
        query.groupByFields = updateFieldInArray(query.groupByFields);
      });
      // console.log("Updated Sheets:", sheets);
    },
    setWorkbookData: (state, action) => {
      // // for viewWorkbook
      // const SheetID = action.payload.sheets[0]?.ID;
      const { _id, sheets, name } = action.payload;
      // console.log(action.payload);
      state._id = action.payload._id;
      state.selectedSheetId = action.payload.selectedSheetId;
      state.name = action.payload.name;
      state.type = action.payload.type;
      state.commonFilters = action.payload.commonFilters;
      state.hierarchy = action.payload.hierarchy;
      state.createdOn = action.payload.createdOn;
      state.folderId = action.payload.folderId;
      state.sheets = sheets;
      state.calculatedFields = action.payload.calculatedFields;
      state.dataSources = action.payload.dataSources;
      state.folderId = action.payload.folderId;
      state.publishId =
        action.payload.publishId.length ? action.payload.publishId : [];
    },
    resetSheet: (state, action) => {
      const { Sheetid, query, customization, actions } = action.payload;
      // console.log({
      //   Sheetid,
      //   query,
      //   customization,
      //   actions,
      // });
      state.commonFilters = state.commonFilters.filter(
        (cItem) => cItem.Sheetid !== Sheetid
      );
      const requiredSheetIndex = state.sheets.findIndex(
        (sheet) => sheet.ID === Sheetid
      );
      // console.log(requiredSheetIndex);
      state.sheets[requiredSheetIndex] = {
        ...state.sheets[requiredSheetIndex],
        query,
        customization,
        actions,
      };
    },
    moveDroppedColumn: (state, action) => {
      const { fromReduxKey, toReduxKey, droppedItem, Sheetid } = action.payload;

      const requiredSheet = state.sheets.find((sheet) => sheet.ID === Sheetid);
      if (!requiredSheet) return;

      let query = requiredSheet.query;

      // Add the item to the destination array
      const itemToAdd = query[fromReduxKey].find(
        (item) => item.nanoId === droppedItem.nanoId
      );
      query[toReduxKey] = [...query[toReduxKey], itemToAdd];

      // Remove the item from the source array
      query[fromReduxKey] = query[fromReduxKey].filter(
        (item) => item.nanoId !== droppedItem.nanoId
      );
    },
    setOffset: (state, action) => {
      const { limit, offset, Sheetid, vizType } = action.payload;
      const requiredSheet = state.sheets.find((sheet) => sheet.ID === Sheetid);
      if (!requiredSheet) return;
      let query = requiredSheet.query;
      // console.log(limit);
      // console.log(offset);
      query["rowLimit"] = limit;
      query["offset"] = offset;
    },
    setFiltersForScreenshot: (state, action) => {
      const { value, activeSheetId } = action.payload;
      const updatedSheets = state.sheets.map((sheet) => {
        if (sheet.ID !== activeSheetId) {
          return {
            ...sheet,
            query: {
              ...sheet.query,
              filters: [...sheet.query.filters, ...value],
            },
          };
        }
        return sheet; // Make sure to return the original sheet if not updating
      });
      // console.log(updatedSheets);
      // console.log(state);
      state.sheets = updatedSheets;
      // console.log(state.sheets);
    },
  },
});

const selectTodoDescriptionsProxy = memoize((state) => {
  const selectedSheetId = state.Workbook.present.selectedSheetId;
  // console.log(selectedSheetId);
  const present = state.Workbook.present.present;
  // console.log(present);
  const sheetsLength = state.Workbook.present.sheets;
  // console.log(sheetsLength.length);

  if (selectedSheetId) {
    return state.Workbook.present.sheets.map(
      (
        { ID, SheetName, VizType, dataSourceID, sheetType, isSheetVisible },
        index
      ) => {
        const selectedSheet = ID === selectedSheetId;
        // console.log(selectedSheet);
        // console.log(isSheetVisible);

        return {
          ID,
          SheetName,
          index,
          VizType,
          isSheetVisible,
          sheetType,
          dataSourceID,
          selectedSheet,
        };
      }
    );
  } else {
    return state.Workbook.present.sheets.map(
      (
        { ID, SheetName, VizType, dataSourceID, sheetType, isSheetVisible },
        index
      ) => {
        const selectedSheet = index === 0;
        return {
          ID,
          SheetName,
          index,
          VizType,
          isSheetVisible,
          sheetType,
          dataSourceID,
          selectedSheet,
        };
      }
    );
  }
});

const getSortProperties = (state, Sheetid) => {
  // console.log(Sheetid);

  // Find the sheet based on the provided Sheetid
  const sheet = state.Workbook.present.sheets.find(
    (sheet) => sheet.ID === Sheetid
  );

  // console.log(sheet);

  // If no sheet is found, return undefined or handle accordingly
  if (!sheet) {
    console.error("getSheetProperty: Sheet not found");
    return undefined;
  }

  const { groupByFields = [] } = sheet.query;
  const { sort } = sheet.actions;
  // console.log(groupByFields);

  // Check if groupByFields has length
  const isColumnsPresent = groupByFields.length;
  // console.log(isColumnsPresent);

  // Return empty array if groupByFields are present, otherwise return the original sort
  if (isColumnsPresent) {
    return [];
  } else {
    return sort;
  }
};

const getL2SheetProperty = (state, Sheetid, parentKey, reduxKey) => {
  // console.log(reduxKey, state, Sheetid, parentKey);
  if (!Sheetid || !parentKey || !reduxKey) {
    console.error(
      "Sheetid, parentKey, and reduxKey are required in the function getL2SheetProperty"
    );
    return undefined;
  }

  const sheet = state.Workbook.present.sheets.find(
    (sheet) => sheet.ID === Sheetid
  );
  // console.log(reduxKey);
  if (!sheet) {
    return undefined;
  }

  if (reduxKey === "filters") {
    return getFilters(state, sheet);
  } else if (reduxKey === "localFilter") {
    //if i just want sheet filters
    return sheet[parentKey]?.filters;
  } else {
    return sheet[parentKey]?.[reduxKey];
  }

  // return sheet[parentKey]?.[reduxKey];
};

const getCommonFilters = (state) => state.Workbook.present.commonFilters;

const getFilters = createSelector(
  [getCommonFilters, (state, sheet) => sheet],
  (commonFilters, sheet) => {
    if (!sheet) {
      console.error("getFilters: Sheet not found");
      return [];
    }
    const sheetsFilters = sheet.query.filters || [];
    const commonFiltersForSheet = commonFilters.filter(
      (obj) => obj.Sheetid === sheet.ID
    );
    return [...sheetsFilters, ...commonFiltersForSheet];
  }
);

//this is used for rendering filter sidebar in dashboard
const getDashboardFilters = (state, dashID, Sheetid) => {
  // console.log("Dashboard ID:", dashID);

  if (!dashID) {
    return "dashboard id not found";
  }
  const sheet = state.Workbook.present.sheets.find(
    (sheet) => sheet.ID === Sheetid
  );
  if (!sheet) {
    console.error("getSheetProperty: Sheet not found");
    return undefined;
  }

  // Loop through each sheet to collect filters that match the dashID
  const filters = getFilters(state, sheet);
  // console.log(filters);
  if (filters.length === 0) {
    return [];
  }
  const dashboardFiltersAccordingToThedashID = filters.filter((filter) =>
    filter.dashboardIDs?.includes(dashID)
  ); // Filter for matching dashboard IDs

  // console.log(dashboardFiltersAccordingToThedashID);
  if (dashboardFiltersAccordingToThedashID.length === 0) {
    return [];
  }

  return dashboardFiltersAccordingToThedashID; // Return the filters that match the dashID
};

const getL1SheetProperty = (state, Sheetid, parentKey) => {
  // console.log(Sheetid, parentKey);
  if (!Sheetid || !parentKey)
    console.log(
      "Sheetid, parentKey are required in the function getL2SheetProperty"
    );
  const sheet = state.Workbook.present.sheets.find(
    (sheet) => sheet.ID === Sheetid
  );
  if (!sheet) {
    // console.log("getSheetProperty: Sheet not found");
    return undefined;
  }
  return sheet[parentKey];
};

const getQueryArray = (state, Sheetid, VizType, uniqueColumnValues) => {
  // Find the sheet with the matching Sheetid
  const sheet = state.Workbook.present.sheets.find(
    (sheet) => sheet.ID === Sheetid
  );

  if (!sheet) {
    // console.log(`No sheet found with ID: ${Sheetid}`);
    return [];
  }

  // Extract the arrays from the sheet's query object
  const { rows = [], groupByFields = [], valueColumns = [] } = sheet.query;
  // Merge the arrays
  // // console.log(VizType);
  // console.log(uniqueColumnValues);
  // // console.log(rows);
  // // console.log(groupByFields);
  // console.log(valueColumns);
  let mergedArray;
  switch (VizType) {
    case "piechart":
    case "rankTile":
    case "combochart":
    case "funnelchart":
    case "table":
    case "disktreechart":
    case "wordcloudchart":
    case "heatmapchart":
    case "bigNumber":
    case "radarchart":
      mergedArray = [...groupByFields, ...valueColumns];
      break;
    case "barchart":
      mergedArray = [...rows, ...valueColumns];
      break;
    case "simpleTable":
      if (groupByFields.length) {
        mergedArray = [...rows, ...uniqueColumnValues];
      } else {
        mergedArray = [...rows, ...valueColumns];
      }
      break;
    case "stackedbarchart":
    case "stackedbarchartimage":
      if (groupByFields.length) {
        mergedArray = [...groupByFields, ...uniqueColumnValues];
      } else {
        mergedArray = [...groupByFields, ...valueColumns];
      }
      break;
    case "linechart":
    case "areachart":
      mergedArray = [...groupByFields, ...uniqueColumnValues];
      break;
    case "scatterchart":
      mergedArray = [...valueColumns];
      break;
    default:
      mergedArray = [];
      break;
  }
  // console.log(mergedArray);
  return mergedArray;
};

const getWorkbookProperty = (state, Key) => {
  if (!Key) {
    // console.log("Key is required in the function getWorkbookProperty");
  }
  const sheet = state.Workbook.present;
  if (!sheet) {
    // console.log("getWorkbookProperty: Workbook not found");
    return undefined;
  }
  return sheet[Key];
};
const activeSheetFilters = (state, activeSheetId) => {
  const sheets = state.Workbook.present.sheets;

  const nonActiveSheets = sheets.filter((sheet) => sheet.ID !== activeSheetId);

  const filtersTargetingActiveSheet = nonActiveSheets.flatMap((sheet) =>
    sheet.query.filters.filter((f) => f.Sheetid === activeSheetId)
  );

  // Filter for unique filters based on FilterID
  const nonActiveSheetFilters = Array.from(
    new Map(filtersTargetingActiveSheet.map((f) => [f.FilterID, f])).values()
  );
  if (Array.isArray(nonActiveSheetFilters)) {
    return nonActiveSheetFilters;
  } else {
    return [];
  }
};

const setWorkbookPropertyAsync = (payload) => (dispatch) => {
  return new Promise((resolve) => {
    dispatch(setWorkbookProperty(payload));
    resolve(); // Resolve after dispatch
  });
};

export {
  getL2SheetProperty,
  getL1SheetProperty,
  getWorkbookProperty,
  getFilters,
  selectTodoDescriptionsProxy,
  getSortProperties,
  getDashboardFilters,
  getQueryArray,
  setWorkbookPropertyAsync,
  activeSheetFilters,
};

export const {
  CreateSheet,
  RemoveSheet,
  setLevel2Properties,
  updateLevel2Properties,
  removeLevel2Properties,
  removeLevel2PropertiesBynanoId,
  updateL1Property,
  setLevel2SliderProperty,
  setWorkbookProperty,
  setDataSource,
  setCommonFilters,
  setFiltersToOtherSheets,
  deleteTemporaryFilters,
  deleteAction,
  setWorkbookData,
  setInitialState,
  isSheetVisible,
  updateCalculatedField,
  updateQueryItemForHierarchy,
  resetSheet,
  moveDroppedColumn,
  setOffset,
  setFiltersForScreenshot,
} = reduxWorkbookSlice.actions;

// Wrap the reducer with undoable, the reducers added here will skip the undo hierarchy
const undoableReducer = undoable(reduxWorkbookSlice.reducer, {
  filter: excludeAction([
    reduxWorkbookSlice.actions.setLevel2SliderProperty.type,
    reduxWorkbookSlice.actions.setDataSource.type,
  ]),
});

export default undoableReducer;
